import { bool, func, oneOf, string } from 'prop-types'
import { memo, useRef, useState } from 'react'
import styled from 'styled-components'
const StyledPasswordInput = styled.div`
	position: relative;
	& input {
		background-color: #ffffff;
		border-color: #e1e1e1;
		border-radius: 10px;
		border-width: 1.5px;
		color: #000000;
		font-weight: 500;
		outline: none;
		transition: 400ms border;
		width: 100%;
		/* 52, 44, 36 */
		font-size: 16px;
		height: 44px;
		padding-left: 15px;
		padding-right: 45px;
		&[data-size='large'] {
			font-size: 18px;
			height: 52px;
			padding-left: 18px;
			padding-right: 18px;
		}
		&[data-size='small'] {
			font-size: 14px;
			height: 36px;
			padding-left: 12px;
			padding-right: 12px;
		}
		&[data-hide-border='true'] {
			border-width: 0;
		}
		&[data-error='true'] {
			border-color: #e41d32;
		}
		&[type='password'] {
			letter-spacing: 2px;
		}
		&[type='text'] {
			letter-spacing: initial;
		}
		&:hover:not(:disabled) {
			border-color: #018ecc;
		}
		&:focus {
			border-color: #018ecc;
		}
		&:disabled {
			background-color: #f4f4f4;
			color: #717171;
			cursor: default;
		}
		&::placeholder {
			color: #717171;
			letter-spacing: initial;
		}
		.dark & {
			background-color: #2e2d2d;
			border-color: #555555;
			color: #e1e1e1;
			&[data-error='true']:not(:disabled) {
				border-color: #e41d32;
			}
			&:hover:not(:disabled) {
				border-color: #018ecc;
			}
			&:focus {
				border-color: #018ecc;
			}
			&:disabled {
				color: #717171;
			}
		}
	}
	& .toggle {
		align-items: center;
		color: var(--main);
		cursor: pointer;
		display: flex;
		justify-content: center;
		position: absolute;
		user-select: none;
		/* 52, 44, 36 */
		border-radius: 18px;
		height: 36px;
		right: 5px;
		top: 4px;
		width: 36px;
		&[data-size='large'] {
			border-radius: 20px;
			height: 40px;
			right: 6px;
			top: 6px;
			width: 40px;
		}
		&[data-size='small'] {
			border-radius: 16px;
			height: 32px;
			right: 5px;
			top: 2px;
			width: 32px;
		}
		&[data-disabled='false'] {
			&:hover {
				background-color: #f1f1f1;
			}
		}
		.dark & {
			&[data-disabled='false'] {
				&:hover {
					background-color: #222222;
				}
			}
		}
	}
`
const Open = () => (
	<svg
		fill='none'
		height='20'
		stroke='currentColor'
		strokeLinecap='round'
		strokeLinejoin='round'
		strokeWidth='2'
		viewBox='0 0 24 24'
		width='20'
	>
		<path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'></path>
		<circle cx='12' cy='12' r='3'></circle>
	</svg>
)
const Close = () => (
	<svg
		fill='none'
		height='20'
		stroke='currentColor'
		strokeLinecap='round'
		strokeLinejoin='round'
		strokeWidth='2'
		viewBox='0 0 24 24'
		width='20'
	>
		<path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24'></path>
		<line x1='1' y1='1' x2='23' y2='23'></line>
	</svg>
)
const PasswordInput = memo(
	({
		'data-cy': dataCY,
		hideBorder = false,
		isDisabled = false,
		isError = false,
		onChange,
		onFocus,
		placeholder = '',
		ref: innerRef,
		size = 'medium',
		value = '',
	}) => {
		const [type, setType] = useState(false)
		const ref = useRef(null)
		const newRef = innerRef ? innerRef : ref
		return (
			<StyledPasswordInput>
				<input
					className='password-input'
					data-cy={dataCY}
					data-error={isError}
					data-hide-border={hideBorder}
					data-size={size}
					disabled={isDisabled}
					onChange={e => onChange(e.target.value)}
					onFocus={onFocus}
					placeholder={placeholder}
					ref={newRef}
					type={type ? 'text' : 'password'}
					value={value}
				/>
				<div
					className='toggle'
					data-disabled={isDisabled}
					data-size={size}
					onClick={() => {
						setType(!type)
						newRef.current.focus()
					}}
				>
					{type ? <Close /> : <Open />}
				</div>
			</StyledPasswordInput>
		)
	}
)
PasswordInput.propTypes = {
	'data-cy': string,
	hideBorder: bool,
	isDisabled: bool,
	isError: bool,
	onChange: func,
	onFocus: func,
	placeholder: string,
	size: oneOf(['large', 'medium', 'small']),
	value: string,
}
export default PasswordInput
